/**
 * PoliceReportRow
 *
 * Renders the police report line item inside the Liability section card.
 * Displays a PoliceReportLink for tiers 1–2 (confirmed/unconfirmed reports)
 * and falls back to a standard ContextRow for tiers 0, 3–6.
 */
import { PoliceReportLink } from '@/components/entity-links';
import { cn } from '@/lib/utils';

import { CallPrepToggle } from './CallPrepToggle';
import { ContextRow } from './CaseContextSection';
import type { PoliceReportTierResult } from './police-report-utils';

interface PoliceReportRowProps {
  policeReportTier: PoliceReportTierResult;
  matterId: string;
  toggleInCallPrep: (sourceId: string, text: string) => void;
  isInCallPrep: (sourceId: string) => boolean;
}

export const PoliceReportRow = ({
  policeReportTier,
  matterId,
  toggleInCallPrep,
  isInCallPrep,
}: PoliceReportRowProps) => {
  // Tiers 1–2: entity-link row with PoliceReportLink
  if (
    (policeReportTier.tier === 1 || policeReportTier.tier === 2) &&
    policeReportTier.report
  ) {
    return (
      <div
        className={cn(
          'group flex items-center justify-between rounded px-2 py-1.5 transition-colors',
          'hover:bg-white dark:hover:bg-zinc-800',
          isInCallPrep(policeReportTier.sourceId) &&
            'bg-amber-50 dark:bg-amber-900/20',
        )}
      >
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-xs font-medium text-zinc-600 dark:text-zinc-400 flex-shrink-0">
            {policeReportTier.label}:
          </span>
          <PoliceReportLink
            entityId={policeReportTier.report.id as string}
            matterId={matterId}
            className="truncate text-xs font-medium"
          >
            {policeReportTier.report.police_department?.name || 'Department'}
          </PoliceReportLink>
          {policeReportTier.report.report_identifier && (
            <span className="text-xs text-zinc-500 dark:text-zinc-400 flex-shrink-0">
              #{policeReportTier.report.report_identifier}
            </span>
          )}
        </div>
        <CallPrepToggle
          sourceId={policeReportTier.sourceId}
          callPrepText={policeReportTier.callPrepText}
          isActive={isInCallPrep(policeReportTier.sourceId)}
          onToggle={toggleInCallPrep}
        />
      </div>
    );
  }

  // Tiers 0, 1 (no report), 3–6: standard ContextRow
  return (
    <>
      <ContextRow
        label="Police Report"
        value={policeReportTier.label}
        sourceId={policeReportTier.sourceId}
        callPrepText={policeReportTier.callPrepText}
        toggleInCallPrep={toggleInCallPrep}
        isInCallPrep={isInCallPrep(policeReportTier.sourceId)}
      />
      {/* Show investigation description for tiers 3–5 when available */}
      {'description' in policeReportTier && policeReportTier.description && (
        <div className="px-2 pb-1">
          <p className="text-xs text-zinc-500 dark:text-zinc-400 font-medium leading-snug">
            {policeReportTier.description}
          </p>
        </div>
      )}
    </>
  );
};
